import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import './BookingPage.css';
import './GroupBooking.css';
import ServiceAccordionItem from './ServiceAccordionItem';
import { useCityPricing } from '../../contexts/CityPricingContext';
import { addToCart, addToWishlist, isInWishlist, CartItem } from '../../utils/cartUtils';
import { getAddresses, getAddressesSync, getDefaultAddress, Address } from '../../utils/addressUtils';
import { authService } from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import { bookingConfig, bookingHoldMessage } from '../../config/bookingConfig';
import bookingService, { UserInterestRequest } from '../../services/bookingService';
import BookingHoldModal from '../BookingHoldModal/BookingHoldModal';
import PlatformSitePriceModal from '../PlatformSitePriceModal';
import { platformSitePricingService, ProcessedPlatformPricing } from '../../services/platformSitePricingService';
import { 
  subServicesData, 
  customerReviews, 
  spiritualTestimonials, 
  featuredPujaServices,
  spiritualPromotions,
  mainServices,
  serviceBenefits,
  SubService,
  Service
} from '../../data/servicesData';
import { servicePlans, ServicePlan } from '../../data/servicePlans';

// Extend ServicePlan to include optional DB pricing data
interface ExtendedServicePlan extends ServicePlan {
  dbPlan?: {
    basePrice: number;
    gstAmount: number;
    totalAmount: number;
  };
  tierLevel?: string; // 'basic' | 'standard' | 'premium' — preserved when plan.id is overwritten by DB merge
}

// Helper function to merge DB pricing with servicePlans.ts details
const getServicePlansWithDynamicPricing = (
  service: SubService,
  cityPricing: any,
  servicePlansData: ServicePlan[]
): ExtendedServicePlan[] => {
  console.log('🔧 getServicePlansWithDynamicPricing Debug:', {
    serviceName: service?.name,
    hasCityPricing: !!cityPricing?.services,
    servicePlansDataLength: servicePlansData.length,
    availableStaticPlans: servicePlansData.map(p => ({ id: p.id, name: p.name }))
  });
  
  // First check if we have city pricing data from DB
  if (cityPricing?.services && service?.name) {
    console.log('🌍 Available city services for matching:', cityPricing.services.map((s: any) => `"${s.serviceName}"`));
    
    const cityService = cityPricing.services.find((s: any) => {
      const serviceName = s.serviceName.toLowerCase().trim();
      const searchName = service.name.toLowerCase().trim();
      
      // More comprehensive matching logic
      return serviceName === searchName || // Exact match
             serviceName.includes(searchName) || // City service contains search name
             searchName.includes(serviceName) || // Search name contains city service
             s.serviceId === service.id; // ID match
    });
    
    console.log('🎯 City service found:', cityService ? `"${cityService.serviceName}" with ${cityService.plans.length} plans` : 'NO MATCH');
    console.log('🔍 Matching details:', {
      searchingFor: service.name,
      searchingForId: service.id,
      foundService: cityService?.serviceName || 'None',
      foundServiceId: cityService?.serviceId || 'None'
    });
    
    if (cityService && cityService.plans.length > 0) {
      console.log('📋 DB Plans available:', cityService.plans.map((p: any) => ({ planCode: p.planCode, basePrice: p.basePrice })));
      console.log('🎯 USING DATABASE PRICING - Creating plans with DB prices and servicePlans.ts details');
      
      // Merge DB pricing with servicePlans.ts details
      return cityService.plans.map((dbPlan: any, index: number) => {
        // More flexible matching for plan codes
        const planCode = (dbPlan.planCode || 'BASIC').toLowerCase();
        console.log(`🔍 Looking for matching static plan for DB plan: "${dbPlan.planCode}" (${planCode})`);
        
        // Try multiple matching strategies with priority order
        let matchingStaticPlan: ServicePlan | undefined = undefined;
        
        // Step 1: Try exact ID match first (highest priority)
        matchingStaticPlan = servicePlansData.find(staticPlan => {
          const staticPlanId = staticPlan.id.toLowerCase();
          return staticPlanId === planCode;
        });
        
        if (!matchingStaticPlan) {
          // Step 2: Try exact plan name match within static plan name
          matchingStaticPlan = servicePlansData.find(staticPlan => {
            const staticPlanName = staticPlan.name.toLowerCase();
            return staticPlanName.includes(planCode);
          });
        }
        
        if (!matchingStaticPlan) {
          // Step 3: Try reverse match (planCode contains staticPlan id/name)
          matchingStaticPlan = servicePlansData.find(staticPlan => {
            const staticPlanName = staticPlan.name.toLowerCase();
            const staticPlanId = staticPlan.id.toLowerCase();
            return planCode.includes(staticPlanId) || planCode.includes(staticPlanName);
          });
        }
        
        if (!matchingStaticPlan) {
          // Step 4: Try special mappings only as last resort
          const planMappings: { [key: string]: string } = {
            'basic': 'basic',
            'standard': 'standard', 
            'premium': 'premium',
            'deluxe': 'premium',
            'ultimate': 'premium'
          };
          
          const mappedPlanId = planMappings[planCode];
          if (mappedPlanId) {
            matchingStaticPlan = servicePlansData.find(staticPlan => {
              return staticPlan.id.toLowerCase() === mappedPlanId;
            });
          }
        }
        
        console.log(`🔍 Plan matching debug for "${dbPlan.planCode}" (${planCode}):`, {
          availableStaticPlans: servicePlansData.map(p => ({ id: p.id, name: p.name })),
          finalMatch: matchingStaticPlan ? { id: matchingStaticPlan.id, name: matchingStaticPlan.name } : null
        });
        
        // Fallback to index-based match if no name/ID match found
        if (!matchingStaticPlan) {
          console.log(`⚠️ No exact match found, trying index-based fallback (index: ${index})`);
          matchingStaticPlan = servicePlansData[index] || servicePlansData[0];
          console.log(`📍 Index-based fallback selected:`, matchingStaticPlan ? { id: matchingStaticPlan.id, name: matchingStaticPlan.name } : 'None available');
        }
        
        console.log(`✅ Matched static plan:`, matchingStaticPlan ? 
          `"${matchingStaticPlan.name}" (${matchingStaticPlan.id})` : 'NONE');
        
        if (matchingStaticPlan) {
          // Use servicePlans.ts details with DB pricing
          const result = {
            ...matchingStaticPlan, // Use all details from servicePlans.ts
            id: `db-${service.id}-${index}`,
            tierLevel: matchingStaticPlan.id, // preserve 'basic'|'standard'|'premium' for CSS tier class
            name: dbPlan.planCode.charAt(0) + dbPlan.planCode.slice(1).toLowerCase(), // Use DB plan name
            price: `₹${Math.round(dbPlan.totalAmount).toLocaleString()}`, // Use GST-inclusive price
            originalPrice: matchingStaticPlan.originalPrice,
            popular: index === 0, // Mark first plan as popular
            dbPlan: dbPlan // Store original DB plan for payment calculations
          } as ExtendedServicePlan;
          
          console.log(`🎯 Final merged plan:`, {
            name: result.name,
            price: result.price,
            materialsCount: result.materials?.length || 0,
            includesCount: result.includes?.length || 0,
            bestForCount: result.bestFor?.length || 0,
            featuresCount: result.features?.length || 0
          });
          
          return result;
        } else {
          console.log('⚠️ No matching static plan found, using fallback');
          // Fallback to basic structure if no matching static plan found
          // Try to use any available plan from servicePlans.ts as a better fallback
          const anyStaticPlan = servicePlansData[0];
          return {
            id: `db-${service.id}-${index}`,
            name: dbPlan.planCode.charAt(0) + dbPlan.planCode.slice(1).toLowerCase(),
            price: `₹${Math.round(dbPlan.totalAmount).toLocaleString()}`, // Use GST-inclusive price
            originalPrice: undefined,
            duration: anyStaticPlan?.duration || '2-3 hours',
            popular: index === 0,
            features: anyStaticPlan?.features || ['Professional puja services', 'Complete ritual setup'],
            priests: anyStaticPlan?.priests || 1,
            materials: anyStaticPlan?.materials || [],
            includes: anyStaticPlan?.includes || [],
            bestFor: anyStaticPlan?.bestFor || [],
            ritualSteps: anyStaticPlan?.ritualSteps || [],
            languages: anyStaticPlan?.languages || ['Hindi', 'English'],
            specialFeatures: anyStaticPlan?.specialFeatures || [],
            minAdvanceBooking: anyStaticPlan?.minAdvanceBooking || '2 days',
            cancellationPolicy: anyStaticPlan?.cancellationPolicy || 'Free cancellation up to 24 hours before service',
            whatThisFeelsLike: anyStaticPlan?.whatThisFeelsLike || ['Divine blessings and spiritual peace', 'Sacred atmosphere filled with positive energy', 'Deep connection with traditional spiritual practices'],
            dbPlan: dbPlan
          } as ExtendedServicePlan;
        }
      });
    }
  }
  
  console.log('📋 Using static servicePlans.ts data as fallback - NO DATABASE PRICING AVAILABLE');
  // Fallback to servicePlans.ts data
  return servicePlansData as ExtendedServicePlan[];
};

// Function to validate if selected address city matches the pricing city
const validateAddressCity = (address: Address | null, selectedCity: any): boolean => {
  if (!address || !selectedCity) return true;
  
  const addressCityName = (address.cityName || '').trim().toLowerCase();
  const selectedCityName = (selectedCity.name || '').trim().toLowerCase();
  const selectedCityCode = (selectedCity.code || '').trim().toUpperCase();
  
  console.log('🏙️ City Validation Debug:', {
    addressCityName,
    selectedCityName, 
    selectedCityCode,
    addressCity: address.cityName,
    selectedCity: selectedCity,
    addressCityOriginal: address.cityName,
    selectedCityOriginal: selectedCity?.name
  });
  
  // Direct name matching (case insensitive)
  if (addressCityName === selectedCityName) {
    return true;
  }
  
  // Special case for Delhi NCR - if selected city is Delhi, allow NCR cities
  if (selectedCityCode === 'DEL' || selectedCityName === 'delhi' || selectedCityName === 'new delhi' || selectedCityName === 'delhi ncr') {
    const delhiNCRCities = [
      'delhi', 'new delhi', 'delhi ncr', 
      'gurugram', 'gurgaon',
      'noida', 'greater noida', 'greater_noida',
      'ghaziabad',
      'faridabad'
    ];
    
    if (delhiNCRCities.includes(addressCityName)) {
      console.log('✅ Delhi NCR city match:', { addressCityName, selectedCity: selectedCityName });
      return true;
    }
  }
  
  // Map address city names to pricing city codes for other cities
  const cityMapping: { [key: string]: string } = {
    'delhi': 'DEL',
    'new delhi': 'DEL', 
    'delhi ncr': 'DEL',
    'gurugram': 'DEL',
    'gurgaon': 'DEL',
    'noida': 'DEL',
    'greater noida': 'DEL',
    'greater_noida': 'DEL',
    'faridabad': 'DEL',
    'ghaziabad': 'DEL',
    'varanasi': 'VNS',
    'benaras': 'VNS',
    'kashi': 'VNS',
    'banaras': 'VNS'
  };
  
  const expectedCityCode = cityMapping[addressCityName];
  
  // If we have a mapping, use it
  if (expectedCityCode) {
    const matches = expectedCityCode === selectedCityCode;
    console.log('🗺️ City mapping match:', { addressCityName, expectedCityCode, selectedCityCode, matches });
    return matches;
  }
  
  // Fallback: check if address city name contains selected city name or vice versa
  if (addressCityName.includes(selectedCityName) || selectedCityName.includes(addressCityName)) {
    console.log('🔍 Substring match found:', { addressCityName, selectedCityName });
    return true;
  }
  
  // Last fallback: compare first 3 letters
  if (addressCityName.length >= 3 && selectedCityName.length >= 3) {
    const firstThreeMatch = addressCityName.substring(0, 3) === selectedCityName.substring(0, 3);
    console.log('🔤 First 3 letters match:', { 
      address: addressCityName.substring(0, 3), 
      selected: selectedCityName.substring(0, 3), 
      matches: firstThreeMatch 
    });
    return firstThreeMatch;
  }
  
  console.log('❌ No city match found:', { addressCityName, selectedCityName, selectedCityCode });
  return false;
};

interface Review {
  id: number;
  customerName: string;
  rating: number;
  comment: string;
  date: string;
  avatar: string;
}

interface Testimonial {
  id: number;
  customerName: string;
  photo: string;
  testimonial: string;
  service: string;
}

interface RecommendedService {
  id: number;
  name: string;
  image: string;
  price: string;
  rating: number;
  trending?: boolean;
}

// Multi-service booking interfaces
interface ServiceBookingConfig {
  cartItem: CartItem;
  selectedPlan: ServicePlan | null;
  selectedDate: string;
  selectedTime: string;
  selectedAddress: Address | null;
  isConfigured: boolean;
  availablePlans?: ServicePlan[]; // Dynamic plans with backend pricing
}

interface GroupBookingState {
  groupBookingId: string;
  services: ServiceBookingConfig[];
  totalAmount: number;
  globalAddress: Address | null; // Shared address for all services
  useGlobalAddress: boolean;
}

const BookingPage = (): React.ReactElement => {
  console.log('📄 BookingPage component rendered');
  
  // Immediate scroll to top on component render
  React.useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // React Router hooks
  const params = useParams();
  
  // Authentication context
  const { user, isAuthenticated } = useAuth();
  
  // City pricing context
  const { cityPricing, selectedCity, isLoadingPricing } = useCityPricing();
  
  // Multi-service booking state
  const [groupBooking, setGroupBooking] = useState<GroupBookingState | null>(null);
  const [activeServiceIndex, setActiveServiceIndex] = useState<number>(0);
  const [useGlobalAddress, setUseGlobalAddress] = useState<boolean>(true);
  const [globalSelectedAddress, setGlobalSelectedAddress] = useState<Address | null>(null);
  const [performAtAntaryatra, setPerformAtAntaryatra] = useState<boolean>(false);
  const [showBookingHoldModal, setShowBookingHoldModal] = useState(false);
  const [cityMismatchError, setCityMismatchError] = useState<string>('');
  const [pendingBookingData, setPendingBookingData] = useState<any>(null);

  // ── Inline notifications (replaces browser alert()) ───────────────────────
  const [bookingError, setBookingError] = useState<string>('');
  const [toast, setToast] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
  const errorBannerRef = React.useRef<HTMLDivElement>(null);

  // Scroll the inline error into view — critical on mobile where the sticky CTA
  // is at the bottom and the error banner may be above the fold.
  const showBookingError = useCallback((message: string) => {
    setBookingError(message);
    setTimeout(() => {
      errorBannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  }, []);

  const showToast = useCallback((type: 'success' | 'error' | 'info', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  }, []);
  
  // Helper function to get plan code from plan name
  const getPlanCode = (planName: string): string => {
    const planCodeMapping: { [key: string]: string } = {
      'basic': 'BASIC',
      'standard': 'STANDARD', 
      'premium': 'PREMIUM',
      'deluxe': 'DELUXE',
      'ultimate': 'ULTIMATE'
    };
    
    const normalizedPlanName = planName.toLowerCase().trim();
    return planCodeMapping[normalizedPlanName] || 'STANDARD';
  };

  // Helper function to get city code from selected city
  const getCityCode = (): string => {
    if (!selectedCity) return 'DEL';
    return selectedCity.code || 'DEL';
  };

  // Platform site pricing function
  // autoConfirm=true: user already opted in from a platform-site landing card
  // (e.g. KashiPujas). Skip the confirmation modal and apply pricing directly.
  const handlePlatformSitePricing = async (serviceId: number, currentPlan: any, autoConfirm?: boolean) => {
    if (!serviceId) return;

    // If platform pricing is already confirmed, just update pricing silently without popup
    if (isPlatformPricingConfirmed && platformSitePricing) {
      // Update platform pricing for the new plan silently
      try {
        const pricing = await platformSitePricingService.getPlatformSitePricing(serviceId);
        setPlatformSitePricing(pricing);
        setConfirmedPlatformSitePlan(pricing.plans[0]);
      } catch (error) {
        console.error('Error updating platform pricing:', error);
      }
      return;
    }

    setIsLoadingPlatformPricing(true);
    try {
      const pricing = await platformSitePricingService.getPlatformSitePricing(serviceId);

      if (autoConfirm) {
        // ── No modal: user navigated from a platform-site card, intent is clear ──
        setPlatformSitePricing(pricing);
        setConfirmedPlatformSitePlan(pricing.plans[0]);
        setIsPlatformPricingConfirmed(true);
        setTempPlatformPricing(null);
      } else {
        // ── Normal flow: store in temp state and show confirmation modal ─────────
        setTempPlatformPricing(pricing);
        setShowPlatformPriceModal(true);
      }

    } catch (error) {
      console.error('Error fetching platform pricing:', error);
      showToast('error', 'Failed to fetch platform site pricing. Please try again.');
      // Reset Antaryatra selection on error
      if (groupBooking) {
        setPerformAtAntaryatra(false);
      } else {
        setPerformSingleAtAntaryatra(false);
      }
    } finally {
      setIsLoadingPlatformPricing(false);
    }
  };

  // Get base amount for pricing display (for confirmed platform pricing or regular pricing)
  const getBaseAmountForDisplay = () => {
    const isAntaryatraSelected = groupBooking ? performAtAntaryatra : performSingleAtAntaryatra;
    if (isAntaryatraSelected && platformSitePricing && effectiveSelectedPlan) {
      // Find matching platform plan for current selected plan
      const matchingPlatformPlan = platformSitePricing.plans.find(platformPlan => {
        const planCode = effectiveSelectedPlan.name.toLowerCase().replace(/[^a-z]/g, '');
        const platformPlanCode = platformPlan.planCode.toLowerCase();
        return platformPlanCode.includes(planCode) || planCode.includes(platformPlanCode);
      });
      return matchingPlatformPlan ? `₹${Math.round(matchingPlatformPlan.totalAmount).toLocaleString()}` : effectiveSelectedPlan.price;
    }
    if (effectiveSelectedPlan) {
      return effectiveSelectedPlan.price;
    }
    return service?.price || '';
  };

  // Handle platform price modal confirmation
  const handlePlatformPriceConfirm = () => {
    if (tempPlatformPricing?.plans[0]) {
      // Now update the actual pricing display after user confirmation
      setPlatformSitePricing(tempPlatformPricing);
      setConfirmedPlatformSitePlan(tempPlatformPricing.plans[0]);
      setIsPlatformPricingConfirmed(true); // Mark as confirmed to avoid future popups
      setTempPlatformPricing(null); // Clear temporary storage
    }
    setShowPlatformPriceModal(false);
  };

  // Handle platform price modal cancellation
  const handlePlatformPriceCancel = () => {
    // Reset Antaryatra selection when user cancels platform pricing
    if (groupBooking) {
      setPerformAtAntaryatra(false);
    } else {
      setPerformSingleAtAntaryatra(false);
    }
    setPlatformSitePricing(null);
    setTempPlatformPricing(null);
    setConfirmedPlatformSitePlan(null);
    setIsPlatformPricingConfirmed(false);
    setShowPlatformPriceModal(false);
  };
  
  // Original single-service states (kept for backward compatibility)
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedPlan, setSelectedPlan] = useState<ExtendedServicePlan | null>(null);
  const [isInUserWishlist, setIsInUserWishlist] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
  const [userAddresses, setUserAddresses] = useState<Address[]>([]);
  const [performSingleAtAntaryatra, setPerformSingleAtAntaryatra] = useState<boolean>(false);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [planModalService, setPlanModalService] = useState<ExtendedServicePlan | null>(null);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  
  // Platform site pricing state
  const [platformSitePricing, setPlatformSitePricing] = useState<ProcessedPlatformPricing | null>(null);
  const [tempPlatformPricing, setTempPlatformPricing] = useState<any>(null);
  const [showPlatformPriceModal, setShowPlatformPriceModal] = useState(false);
  const [confirmedPlatformSitePlan, setConfirmedPlatformSitePlan] = useState<any>(null);
  const [isLoadingPlatformPricing, setIsLoadingPlatformPricing] = useState(false);
  const [isPlatformPricingConfirmed, setIsPlatformPricingConfirmed] = useState<boolean>(false);
  
  const navigate = useNavigate();
  const location = useLocation();

  // Handle accordion toggle with scroll behavior
  const handleAccordionToggle = (index: number) => {
    const newActiveIndex = activeServiceIndex === index ? -1 : index;
    setActiveServiceIndex(newActiveIndex);
    
    // Scroll to the accordion item if it's being opened
    if (newActiveIndex === index) {
      // Use setTimeout to allow the DOM to update before scrolling
      setTimeout(() => {
        const accordionElement = document.querySelector(`[data-accordion-index="${index}"]`);
        if (accordionElement) {
          accordionElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start'
          });
        }
      }, 100);
    }
  };

  // Handle cart booking state
  const cartBookingState = location.state as { 
    fromCart?: boolean; 
    cartItems?: CartItem[]; 
    totalAmount?: number;
    preferPlatformSite?: boolean; // set by KashiPujas & other platform-site landing cards
  } | null;

  const { serviceCategory, serviceType } = useParams<{ 
    serviceCategory: string; 
    serviceType: string; 
  }>();

  // Handle cart booking vs regular service booking
  let service: SubService | null;
  let mainService: Service | null;
  
  if (cartBookingState?.fromCart && cartBookingState.cartItems && cartBookingState.cartItems.length > 0) {
    // For cart booking, create a virtual service from the first cart item
    const firstCartItem = cartBookingState.cartItems[0];
    service = {
      id: parseInt(firstCartItem.id.replace('service-', '')) || 1,
      name: firstCartItem.name,
      icon: '🛒',
      image: firstCartItem.image || '/images/puja_service.jpg',
      description: `Multiple services from cart: ${cartBookingState.cartItems.map(item => item.name).join(', ')}`,
      price: cartBookingState.totalAmount?.toString() || firstCartItem.price,
      duration: '2-4 hours',
      rating: 4.8,
      bookingUrl: '/cart-booking',
      includes: cartBookingState.cartItems.map(item => `${item.name} (${item.duration || 'Standard duration'})`),
      bestFor: [`${cartBookingState.cartItems.length} selected services`, 'Professional puja services', 'Complete ritual setup']
    };
    
    mainService = {
      id: 'cart-booking',
      name: 'Cart Services',
      description: 'Multiple selected services',
      image: '/images/puja_service.jpg',
      priceRange: `₹${cartBookingState.totalAmount || 'Variable'}`,
      category: 'Multiple Services'
    };
  } else {
    // Regular service booking
    console.log('🔍 BookingPage Debug - serviceCategory:', serviceCategory, 'serviceType:', serviceType);
    const allSubServices = serviceCategory ? subServicesData[serviceCategory] || [] : [];
    console.log('🔍 BookingPage Debug - allSubServices for category:', allSubServices);
    service = allSubServices.find(s => 
      s.name.toLowerCase().replace(/\s+/g, '-') === serviceType
    ) || null;
    console.log('🔍 BookingPage Debug - found service:', service);
    mainService = mainServices.find(s => s.id === serviceCategory) || null;
    console.log('🔍 BookingPage Debug - found mainService:', mainService);
  }

  // TypeScript doesn't realize service is guaranteed non-null here due to early return logic
  const currentService = service!;
  const currentMainService = mainService!;

  // Use puja-specific data
  const reviews = customerReviews;
  const testimonials = spiritualTestimonials;
  const recommendedServices = featuredPujaServices;
  const promotions = spiritualPromotions;

  // Get service-specific plans and benefits (with null checks)
  const serviceKey = service?.name.toLowerCase().replace(/\s+/g, '-') || '';
  let staticPlans = servicePlans[serviceKey] || [];
  
  console.log('🔧 Service Plans Debug:', {
    serviceName: service?.name,
    serviceKey: serviceKey,
    staticPlansFound: staticPlans.length,
    availableServiceKeys: Object.keys(servicePlans).slice(0, 5), // Show first 5 keys
    totalServicePlansCount: Object.keys(servicePlans).length
  });
  
  // If no exact match, try flexible matching
  if (staticPlans.length === 0 && service?.name) {
    console.log('🔍 No exact match found, trying flexible matching...');
    const flexibleKeys = Object.keys(servicePlans).filter(key => {
      const keyWords = key.split('-');
      const serviceWords = service!.name.toLowerCase().split(' '); // Use non-null assertion since we checked service?.name above
      return keyWords.some(keyWord => serviceWords.some(serviceWord => 
        serviceWord.includes(keyWord) || keyWord.includes(serviceWord)
      ));
    });
    console.log('🎯 Flexible matches found:', flexibleKeys);
    if (flexibleKeys.length > 0) {
      staticPlans = servicePlans[flexibleKeys[0]] || [];
      console.log('✅ Using flexible match:', flexibleKeys[0], 'with', staticPlans.length, 'plans');
    }
  }
  
  // If we have city pricing and this is a cart booking, try to use dynamic pricing
  let dynamicPlans = staticPlans;
  if (cartBookingState?.fromCart && cityPricing?.services && service && service.name) {
    const serviceName = service.name; // Extract to a local variable to satisfy TypeScript
    console.log('🔍 Looking for city pricing for main service:', serviceName);
    console.log('🌍 Available city services:', cityPricing.services.map(s => `"${s.serviceName}"`));
    console.log('🔄 Cart booking detected, service object:', { name: service?.name, fromCart: cartBookingState?.fromCart });
    
    const cityService = cityPricing.services.find(s => {
      const cityServiceName = s.serviceName.toLowerCase().trim();
      const searchName = serviceName.toLowerCase().trim();
      
      // Remove common words that might differ
      const normalizeServiceName = (name: string) => {
        return name
          .replace(/pujan|puja|pooja/g, '') // Remove puja variants
          .replace(/maha|mah/g, 'maha') // Normalize maha
          .replace(/mrityunjaya|mritunjaya/g, 'mrityunjaya') // Normalize spelling
          .replace(/\s+/g, ' ') // Normalize spaces
          .trim();
      };
      
      const normalizedCityName = normalizeServiceName(cityServiceName);
      const normalizedSearchName = normalizeServiceName(searchName);
      
      // Try multiple matching strategies
      return cityServiceName === searchName || // Exact match
             cityServiceName.includes(searchName) || // City service contains search
             searchName.includes(cityServiceName) || // Search contains city service
             normalizedCityName.includes(normalizedSearchName) || // Normalized contains
             normalizedSearchName.includes(normalizedCityName); // Reverse normalized contains
    });
    
    console.log('🎯 Found city service match:', cityService ? `"${cityService.serviceName}" with ${cityService.plans.length} plans` : 'NO MATCH');
    
    if (cityService && cityService.plans.length > 0) {
      // Get static plans for this service to merge with DB pricing
      const serviceKey = service?.name.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '') || '';
      const staticPlansForService = servicePlans[serviceKey] || [];
      
      // Merge city pricing with servicePlans.ts details
      dynamicPlans = cityService.plans.map((cityPlan, index) => {
        // Find matching plan from servicePlans.ts based on plan code
        const planCode = (cityPlan.planCode || 'BASIC').toLowerCase();
        const matchingStaticPlan = staticPlansForService.find(staticPlan => 
          staticPlan.name.toLowerCase().includes(planCode) ||
          staticPlan.id.toLowerCase().includes(planCode)
        ) || staticPlansForService[index] || staticPlansForService[0];
        
        if (matchingStaticPlan) {
          // Use servicePlans.ts details with DB pricing
          return {
            ...matchingStaticPlan, // Use all details from servicePlans.ts
            id: cityPlan.planCode.toLowerCase(),
            name: cityPlan.planCode.charAt(0) + cityPlan.planCode.slice(1).toLowerCase(),
            price: `₹${Math.round(cityPlan.totalAmount).toLocaleString()}`, // Use GST-inclusive price
            originalPrice: matchingStaticPlan.originalPrice,
            popular: index === 0,
            // Add dbPlan for dynamic pricing
            dbPlan: {
              basePrice: cityPlan.basePrice,
              gstAmount: cityPlan.gstAmount,
              totalAmount: cityPlan.totalAmount
            }
          };
        } else {
          // Fallback to basic structure if no matching static plan found
          // Try to use any available plan from static plans as a better fallback
          const anyStaticPlan = staticPlansForService[0];
          return {
            id: cityPlan.planCode.toLowerCase(),
            name: cityPlan.planCode.charAt(0) + cityPlan.planCode.slice(1).toLowerCase(),
            price: `₹${Math.round(cityPlan.totalAmount).toLocaleString()}`, // Use GST-inclusive price
            originalPrice: undefined,
            duration: anyStaticPlan?.duration || '60 minutes',
            popular: index === 0,
            features: anyStaticPlan?.features || ['Professional puja services', 'Complete ritual setup'],
            priests: anyStaticPlan?.priests || 1,
            includes: anyStaticPlan?.includes || [],
            materials: anyStaticPlan?.materials || [],
            ritualSteps: anyStaticPlan?.ritualSteps || [],
            bestFor: anyStaticPlan?.bestFor || [],
            languages: anyStaticPlan?.languages || ['Hindi', 'English'],
            specialFeatures: anyStaticPlan?.specialFeatures || [],
            minAdvanceBooking: anyStaticPlan?.minAdvanceBooking || '2 days',
            cancellationPolicy: anyStaticPlan?.cancellationPolicy || 'Free cancellation up to 24 hours before service',
            whatThisFeelsLike: anyStaticPlan?.whatThisFeelsLike || ['Divine blessings and spiritual peace', 'Sacred atmosphere filled with positive energy', 'Deep connection with traditional spiritual practices'],
            // Add dbPlan for dynamic pricing
            dbPlan: {
              basePrice: cityPlan.basePrice,
              gstAmount: cityPlan.gstAmount,
              totalAmount: cityPlan.totalAmount
            }
          };
        }
      });
    } else {
      console.log('⚠️ No city pricing match found for main service:', serviceName);
    }
  } else if (!cartBookingState?.fromCart) {
    // For regular (non-cart) bookings, use existing dynamic pricing logic
    dynamicPlans = service ? getServicePlansWithDynamicPricing(service, cityPricing, staticPlans) : staticPlans;
  }
  
  const plans = dynamicPlans;
  console.log('📋 Final plans for display:', plans.map(p => ({ name: p.name, price: p.price, hasDbPlan: !!(p as ExtendedServicePlan).dbPlan })));
  console.log('💰 PRICING SOURCE SUMMARY:', {
    totalPlans: plans.length,
    plansWithDbPricing: plans.filter(p => !!(p as ExtendedServicePlan).dbPlan).length,
    plansWithStaticPricing: plans.filter(p => !(p as ExtendedServicePlan).dbPlan).length,
    pricingSource: plans.some(p => !!(p as ExtendedServicePlan).dbPlan) ? '🏦 DATABASE' : '📄 STATIC (servicePlans.ts)'
  });
  const benefits = serviceBenefits[serviceKey] || [];

  // Resolve a visible default plan synchronously so mobile sticky CTA never falls back
  // to service-level static price when plan cards are already available.
  const visiblePlans = plans.filter(p => !(p as any).hidden);
  const defaultDisplayPlan = visiblePlans.find(p => p.popular) ?? visiblePlans[0] ?? plans[0] ?? null;
  const matchingSelectedPlan = selectedPlan
    ? plans.find(
        plan =>
          plan.id === selectedPlan.id ||
          plan.name.toLowerCase() === selectedPlan.name.toLowerCase()
      ) || null
    : null;

  // Keep a resolved current plan for UI/price display even before state sync effect runs.
  const effectiveSelectedPlan: ExtendedServicePlan | null =
    (matchingSelectedPlan as ExtendedServicePlan | null) ??
    (defaultDisplayPlan as ExtendedServicePlan | null);

  const isAntaryatraActive = groupBooking ? performAtAntaryatra : performSingleAtAntaryatra;
  const fromPrice = (() => {
    if (!effectiveSelectedPlan) return service?.price || '';
    if (isAntaryatraActive && platformSitePricing) {
      const planCode = effectiveSelectedPlan.name.toLowerCase().replace(/[^a-z]/g, '');
      const match = platformSitePricing.plans.find(pp => {
        const ppCode = pp.planCode.toLowerCase();
        return ppCode.includes(planCode) || planCode.includes(ppCode);
      });
      return match ? `₹${Math.round(match.totalAmount).toLocaleString()}` : effectiveSelectedPlan.price;
    }
    return effectiveSelectedPlan.price;
  })();

  // Legacy helper kept for Antaryatra / group booking paths that call it directly
  const getCurrentPrice = (): string => fromPrice;
  
  // Helper function to get total amount including GST for payment
  const getTotalAmountForPayment = (): number => {
    if (selectedPlan && selectedPlan.dbPlan) {
      // If we have DB pricing, use the total amount
      return selectedPlan.dbPlan.totalAmount;
    } else if (selectedPlan) {
      // For static pricing, calculate GST (18%)
      const basePrice = parseFloat(selectedPlan.price.replace(/[^0-9.-]+/g, '') || '0');
      const gstAmount = basePrice * 0.18;
      return basePrice + gstAmount;
    }
    return 0;
  };
  
  const currentPrice = getCurrentPrice();
  const showServiceTrustSection = Boolean(currentService?.showServiceTrustSection);
  const serviceTrustBadgeText = currentService?.serviceTrustBadgeText || (currentService?.platformSitePreferred ? 'Participate from Anywhere in the World' : '');
  const serviceTrustSummaryBadges = currentService?.serviceTrustSummaryBadges || [];
  const serviceWhyBookPoints = currentService?.whyBookWithAntaryatraPoints || [];
  const stickyCtaPrimaryLabel = effectiveSelectedPlan ? 'Book Now' : 'Select Package';
  const stickyCtaSecondaryLabel = effectiveSelectedPlan ? getBaseAmountForDisplay() : 'Choose a package';

  // Scroll to top when component mounts or route parameters change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [serviceCategory, serviceType, location.pathname]);

  // Initialize group booking state for multi-service bookings
  useEffect(() => {
    if (cartBookingState?.fromCart && cartBookingState.cartItems && cartBookingState.cartItems.length > 1) {
      const groupBookingId = `GROUP_${Date.now()}`;
      // Create service booking configurations with default plans
      const services: ServiceBookingConfig[] = cartBookingState.cartItems.map(item => {
        // Get city-specific service plans if available
        let itemPlans: ServicePlan[] = [];
        
        if (cityPricing?.services) {
          // Try to find matching service in city pricing with better matching
          console.log('🔍 Looking for city pricing for cart item:', `"${item.name}"`);
          console.log('🌍 Available city services for comparison:');
          cityPricing.services.forEach((s, index) => {
            console.log(`  ${index + 1}. "${s.serviceName}"`);
          });
          
          const cityService = cityPricing.services.find(s => {
            const serviceName = s.serviceName.toLowerCase().trim();
            const itemName = item.name.toLowerCase().trim();
            
            // Remove common words that might differ
            const normalizeServiceName = (name: string) => {
              return name
                .replace(/pujan|puja|pooja/g, '') // Remove puja variants
                .replace(/maha|mah/g, 'maha') // Normalize maha
                .replace(/mrityunjaya|mritunjaya/g, 'mrityunjaya') // Normalize spelling
                .replace(/\s+/g, ' ') // Normalize spaces
                .trim();
            };
            
            const normalizedServiceName = normalizeServiceName(serviceName);
            const normalizedItemName = normalizeServiceName(itemName);
            
            // Try multiple matching strategies
            return serviceName === itemName || // Exact match
                   serviceName.includes(itemName) || // Service contains item
                   itemName.includes(serviceName) || // Item contains service
                   normalizedServiceName.includes(normalizedItemName) || // Normalized contains
                   normalizedItemName.includes(normalizedServiceName); // Reverse normalized contains
          });
          
          console.log('🎯 Found city service match:', cityService ? `"${cityService.serviceName}" with ${cityService.plans.length} plans` : 'NO MATCH');
          
          if (cityService && cityService.plans.length > 0) {
            // Get static plans for this service to merge with DB pricing
            const serviceKey = item.name.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');
            const staticPlansForService = servicePlans[serviceKey] || [];
            
            // Merge city pricing with servicePlans.ts details
            itemPlans = cityService.plans.map((cityPlan, planIndex) => {
              // Find matching plan from servicePlans.ts based on plan code
              const planCode = (cityPlan.planCode || 'BASIC').toLowerCase();
              const matchingStaticPlan = staticPlansForService.find(staticPlan => 
                staticPlan.name.toLowerCase().includes(planCode) ||
                staticPlan.id.toLowerCase().includes(planCode)
              ) || staticPlansForService[planIndex] || staticPlansForService[0];
              
              if (matchingStaticPlan) {
                // Use servicePlans.ts details with DB pricing
                return {
                  ...matchingStaticPlan, // Use all details from servicePlans.ts
                  id: cityPlan.planCode.toLowerCase(),
                  name: cityPlan.planCode.charAt(0) + cityPlan.planCode.slice(1).toLowerCase(),
                  price: `₹${Math.round(cityPlan.totalAmount).toLocaleString()}`, // Use GST-inclusive price
                  originalPrice: matchingStaticPlan.originalPrice,
                  popular: planIndex === 0
                };
              } else {
                // Fallback to basic structure if no matching static plan found
                // Try to use any available plan from static plans as a better fallback
                const anyStaticPlan = staticPlansForService[0];
                return {
                  id: cityPlan.planCode.toLowerCase(),
                  name: cityPlan.planCode.charAt(0) + cityPlan.planCode.slice(1).toLowerCase(),
                  price: `₹${Math.round(cityPlan.totalAmount).toLocaleString()}`, // Use GST-inclusive price
                  originalPrice: undefined,
                  duration: anyStaticPlan?.duration || '60 minutes',
                  popular: planIndex === 0,
                  features: anyStaticPlan?.features || ['Professional puja services', 'Complete ritual setup'],
                  priests: anyStaticPlan?.priests || 1,
                  includes: anyStaticPlan?.includes || [],
                  materials: anyStaticPlan?.materials || [],
                  ritualSteps: anyStaticPlan?.ritualSteps || [],
                  bestFor: anyStaticPlan?.bestFor || [],
                  languages: anyStaticPlan?.languages || ['Hindi', 'English'],
                  specialFeatures: anyStaticPlan?.specialFeatures || [],
                  minAdvanceBooking: anyStaticPlan?.minAdvanceBooking || '2 days',
                  cancellationPolicy: anyStaticPlan?.cancellationPolicy || 'Free cancellation up to 24 hours before service',
                  whatThisFeelsLike: anyStaticPlan?.whatThisFeelsLike || ['Divine blessings and spiritual peace', 'Sacred atmosphere filled with positive energy', 'Deep connection with traditional spiritual practices']
                };
              }
            });
          }
        }
        
        // Fallback to static plans if no city pricing available
        if (itemPlans.length === 0) {
          console.log('⚠️ No city pricing found for item:', item.name, '- falling back to static plans');
          const serviceKey = item.name.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');
          console.log('🗝️ Using static service key:', serviceKey);
          itemPlans = servicePlans[serviceKey] || [];
          console.log('📦 Found static plans:', itemPlans.length);
          
          // If still no plans, try a more flexible key matching
          if (itemPlans.length === 0) {
            console.log('🔍 Trying flexible key matching...');
            const flexibleKeys = Object.keys(servicePlans).filter(key => {
              const keyWords = key.split('-');
              const itemWords = item.name.toLowerCase().split(' ');
              return keyWords.some(keyWord => itemWords.some(itemWord => 
                itemWord.includes(keyWord) || keyWord.includes(itemWord)
              ));
            });
            console.log('🎯 Flexible matches found:', flexibleKeys);
            if (flexibleKeys.length > 0) {
              itemPlans = servicePlans[flexibleKeys[0]] || [];
              console.log('✅ Using flexible match:', flexibleKeys[0], 'with', itemPlans.length, 'plans');
            }
          }
        }
        
        const defaultPlan = itemPlans.find((plan: ServicePlan) => plan.popular) || itemPlans[0] || null;
        console.log('🎯 Selected default plan for', item.name, ':', defaultPlan?.name, 'at price:', defaultPlan?.price);
        console.log('📦 Available plans for', item.name, ':', itemPlans.map(p => ({ name: p.name, price: p.price })));
        
        return {
          cartItem: item,
          selectedPlan: defaultPlan, // Set default plan immediately
          selectedDate: '',
          selectedTime: '',
          selectedAddress: null,
          isConfigured: false,
          availablePlans: itemPlans // Store the dynamic plans for use in ServiceAccordionItem
        };
      });
      
      // Calculate initial total based on selected plans (not cart prices)
      const initialTotalAmount = services.reduce((sum, serviceConfig) => {
        if (serviceConfig.selectedPlan) {
          const planPrice = parseFloat(serviceConfig.selectedPlan.price.replace(/[^0-9.-]+/g, '') || '0');
          return sum + planPrice;
        }
        // Fallback to cart price if no plan
        const cartPrice = parseFloat(serviceConfig.cartItem.price.replace(/[^0-9.-]+/g, '') || '0');
        return sum + cartPrice;
      }, 0);
      
      setGroupBooking({
        groupBookingId,
        services,
        totalAmount: initialTotalAmount, // Use calculated total instead of cart total
        globalAddress: null,
        useGlobalAddress: true
      });
      
      console.log('🎯 Initialized group booking for', services.length, 'services');
    }
  }, [cartBookingState]);

  // Function to calculate total base amount (sum of displayed prices)
  const calculateTotalAmount = useCallback((services: ServiceBookingConfig[]) => {
    console.log('🧮 Starting total calculation for', services.length, 'services');
    
    const baseAmount = services.reduce((sum, serviceConfig, index) => {
      console.log(`\n📋 Service ${index + 1}: "${serviceConfig.cartItem.name}"`);
      
      if (serviceConfig.selectedPlan) {
        const extendedPlan = serviceConfig.selectedPlan as ExtendedServicePlan;
        let serviceAmount = 0;
        
        if (extendedPlan.dbPlan) {
          // Use GST-inclusive totalAmount so the displayed total matches the per-plan card price
          serviceAmount = extendedPlan.dbPlan.totalAmount;
          console.log('💰 Using GST-inclusive totalAmount:', serviceAmount);
          console.log('💰 dbPlan full details:', { 
            basePrice: extendedPlan.dbPlan.basePrice, 
            gstAmount: extendedPlan.dbPlan.gstAmount || 'N/A', 
            totalAmount: extendedPlan.dbPlan.totalAmount || 'N/A' 
          });
        } else {
          // For static pricing, use the displayed price as-is
          const priceString = serviceConfig.selectedPlan.price;
          serviceAmount = parseFloat(priceString.replace(/[^0-9.-]+/g, '') || '0');
          console.log('💰 Using static display price:');
          console.log('  - Price string:', priceString);
          console.log('  - Parsed amount:', serviceAmount);
        }
        
        console.log(`✅ Adding ${serviceAmount} to total (running sum: ${sum} + ${serviceAmount} = ${sum + serviceAmount})`);
        return sum + serviceAmount;
      } else {
        // Fallback to cart item price (use as-is)
        const cartPrice = parseFloat(serviceConfig.cartItem.price.replace(/[^0-9.-]+/g, '') || '0');
        console.log('💰 No selected plan, using cart item price:', cartPrice);
        console.log(`✅ Adding ${cartPrice} to total (running sum: ${sum} + ${cartPrice} = ${sum + cartPrice})`);
        return sum + cartPrice;
      }
    }, 0);
    
    console.log('\n🎯 FINAL CALCULATION SUMMARY:');
    console.log('Total services:', services.length);
    console.log('Final amount:', baseAmount);
    
    // Detailed breakdown for debugging
    const breakdown = services.map((s, i) => {
      let amount = 0;
      let source = '';
      
      if (s.selectedPlan) {
        const extendedPlan = s.selectedPlan as ExtendedServicePlan;
        if (extendedPlan.dbPlan) {
          amount = extendedPlan.dbPlan.totalAmount;
          source = 'backend (incl. GST)';
        } else {
          amount = parseFloat(s.selectedPlan.price.replace(/[^0-9.-]+/g, '') || '0');
          source = 'static';
        }
      } else {
        amount = parseFloat(s.cartItem.price.replace(/[^0-9.-]+/g, '') || '0');
        source = 'cart';
      }
      
      return { 
        index: i + 1,
        name: s.cartItem.name, 
        selectedPlan: s.selectedPlan?.name || 'None',
        amount: amount,
        source: source
      };
    });
    
    console.table(breakdown);
    console.log('Sum check:', breakdown.reduce((sum, item) => sum + item.amount, 0));
    
    return Math.round(baseAmount);
  }, []);

  // Update total amount when services change
  useEffect(() => {
    if (groupBooking?.services) {
      const newTotalAmount = calculateTotalAmount(groupBooking.services);
      if (newTotalAmount !== groupBooking.totalAmount) {
        setGroupBooking(prev => prev ? {
          ...prev,
          totalAmount: newTotalAmount
        } : null);
      }
    }
  }, [groupBooking?.services.length, groupBooking?.services.map(s => s.selectedPlan?.id).join(','), calculateTotalAmount]);

  // Recalculate configuration status when global address changes
  useEffect(() => {
    if (groupBooking && useGlobalAddress && globalSelectedAddress) {
      // Validate city match
      if (!validateAddressCity(globalSelectedAddress, selectedCity)) {
        const isDelhi = selectedCity?.code === 'DEL' || selectedCity?.name?.toLowerCase().includes('delhi');
        const message = isDelhi 
          ? `Selected address is in ${globalSelectedAddress.cityName} but you're viewing prices for ${selectedCity?.name}. For Delhi pricing, please select an address in Delhi, Gurugram, Noida, Greater Noida, or Ghaziabad.`
          : `Selected address is in ${globalSelectedAddress.cityName} but you're viewing prices for ${selectedCity?.name}. Please select an address in ${selectedCity?.name} or add a new address for the correct city.`;
        setCityMismatchError(message);
      } else {
        setCityMismatchError('');
      }
      
      const needsUpdate = groupBooking.services.some(service => {
        const shouldBeConfigured = !!(
          service.selectedPlan &&
          service.selectedDate &&
          service.selectedTime &&
          globalSelectedAddress
        );
        return service.isConfigured !== shouldBeConfigured;
      });

      if (needsUpdate) {
        const updatedServices = groupBooking.services.map(service => {
          const isConfigured = !!(
            service.selectedPlan &&
            service.selectedDate &&
            service.selectedTime &&
            globalSelectedAddress
          );
          return { ...service, isConfigured };
        });
        
        setGroupBooking(prev => prev ? {
          ...prev,
          services: updatedServices
        } : null);
      }
    }
  }, [globalSelectedAddress, selectedCity, useGlobalAddress, groupBooking]);

  // Function to update service configuration in group booking
  const updateServiceConfig = useCallback((index: number, updates: Partial<ServiceBookingConfig>) => {
    if (!groupBooking) return;
    
    const updatedServices = [...groupBooking.services];
    updatedServices[index] = { ...updatedServices[index], ...updates };
    
    // Check if service is fully configured
    const service = updatedServices[index];
    const isConfigured = !!(
      service.selectedPlan &&
      service.selectedDate &&
      service.selectedTime &&
      (performAtAntaryatra || (useGlobalAddress ? globalSelectedAddress : service.selectedAddress))
    );
    
    updatedServices[index].isConfigured = isConfigured;
    
    // Calculate new total amount using the centralized function (includes GST)
    const newTotalAmount = calculateTotalAmount(updatedServices);
    
    setGroupBooking({
      ...groupBooking,
      services: updatedServices,
      totalAmount: newTotalAmount // Update total amount
    });
  }, [groupBooking, useGlobalAddress, globalSelectedAddress, performAtAntaryatra, calculateTotalAmount]);

  // Set default plan and keep it in sync when DB pricing loads
  useEffect(() => {
    if (plans.length === 0 || !effectiveSelectedPlan) return;

    const needsSelectionSync =
      !selectedPlan ||
      !matchingSelectedPlan ||
      selectedPlan.id !== effectiveSelectedPlan.id ||
      selectedPlan.price !== effectiveSelectedPlan.price;

    if (needsSelectionSync) {
      setSelectedPlan(effectiveSelectedPlan);

      // ── Auto-init Antaryatra site ────────────────────────────────────────
      // siteOnly: service can ONLY be performed at Antaryatra site — always lock ON
      if (currentService?.siteOnly && !groupBooking) {
        setPerformSingleAtAntaryatra(true);
        const serviceId =
          typeof currentService.id === 'number'
            ? currentService.id
            : parseInt(String(currentService.id)) || 1;
        (async () => { await handlePlatformSitePricing(serviceId, effectiveSelectedPlan, true); })();
      } else if (cartBookingState?.preferPlatformSite && !groupBooking && currentService) {
        setPerformSingleAtAntaryatra(true);
        const serviceId =
          typeof currentService.id === 'number'
            ? currentService.id
            : parseInt(String(currentService.id)) || 1;
        // autoConfirm=true — user already chose Antaryatra from the landing card,
        // so prices apply directly with no modal interruption.
        (async () => { await handlePlatformSitePricing(serviceId, effectiveSelectedPlan, true); })();
      }
      // ────────────────────────────────────────────────────────────────────
    }
  }, [
    plans,
    selectedPlan,
    matchingSelectedPlan,
    effectiveSelectedPlan,
    currentService,
    groupBooking,
    cartBookingState?.preferPlatformSite
  ]);

  // Check if service is in wishlist
  useEffect(() => {
    if (currentService) {
      setIsInUserWishlist(isInWishlist(currentService.id.toString()));
    }
  }, [currentService]);

  // Function to load addresses
  const loadAddresses = useCallback(async () => {
    setIsLoadingAddresses(true);
    try {
      const isAuthenticated = authService.isAuthenticated();
      
      if (isAuthenticated) {
        const freshAddresses = await getAddresses();
        console.log('📋 BookingPage received addresses:', freshAddresses);
        setUserAddresses(freshAddresses);
        console.log('📋 BookingPage state set with:', freshAddresses.length, 'addresses');
        
        if (freshAddresses.length > 0) {
          const defaultAddress = freshAddresses.find(addr => addr.isDefault) || freshAddresses[0];
          console.log('🏠 BookingPage setting selected address:', defaultAddress);
          setSelectedAddress(defaultAddress);
        }
      } else {
        const cachedAddresses = getAddressesSync();
        setUserAddresses(cachedAddresses);
        
        const defaultAddress = getDefaultAddress();
        if (defaultAddress) {
          setSelectedAddress(defaultAddress);
        }
      }
    } catch (error) {
      console.error('Error loading addresses:', error);
      const cachedAddresses = getAddressesSync();
      setUserAddresses(cachedAddresses);
      
      const defaultAddress = getDefaultAddress();
      if (defaultAddress) {
        setSelectedAddress(defaultAddress);
      }
    } finally {
      setIsLoadingAddresses(false);
    }
  }, []);

  // Load user addresses on component mount
  useEffect(() => {
    loadAddresses();
  }, [loadAddresses]);

  // Helper function to get minimum booking date based on configuration
  const getMinBookingDate = () => {
    const today = new Date();
    today.setDate(today.getDate() + bookingConfig.minDaysFromToday);
    return today.toISOString().split('T')[0];
  };

  // Handle booking hold modal confirmation
  const handleBookingHoldConfirm = async () => {
    if (!pendingBookingData) {
      setShowBookingHoldModal(false);
      return;
    }

    try {
      // Get current user - use async/await since getCurrentUser is async
      const currentUser = await authService.getCurrentUser();
      console.log('🔍 Current user object for interest capture:', {
        id: currentUser?.id,
        userUuid: currentUser?.userUuid,
        email: currentUser?.email,
        username: currentUser?.username
      });
      
      // Prioritize userUuid field first, then fallback to id if no userUuid exists
      const userUuid = currentUser?.userUuid || currentUser?.id;
      
      if (!userUuid) {
        showToast('info', 'Please log in to capture your interest');
        return;
      }

      console.log('🎯 Using userUuid for interest capture:', userUuid);

      const userInterest: UserInterestRequest = {
        userUuid,
        serviceId: pendingBookingData.serviceId,
        serviceName: pendingBookingData.serviceName,
        selectedPlan: pendingBookingData.selectedPlan?.name || 'standard',
        requestDate: new Date(`${pendingBookingData.selectedDate}T${pendingBookingData.selectedTime}`).toISOString()
      };

      await bookingService.captureUserInterest(userInterest);
      console.log('✅ User interest captured successfully');
      
      // Close modal and clear pending data
      setShowBookingHoldModal(false);
      setPendingBookingData(null);
      
      // Navigate to orders page or show success message
      navigate('/orders', { 
        state: { 
          message: 'Your interest has been captured. We will contact you soon with available dates.' 
        } 
      });
    } catch (error) {
      console.error('❌ Error capturing user interest:', error);
      showToast('error', 'Failed to capture your interest. Please try again.');
    }
  };

  // Early return if service not found for regular bookings
  if (!cartBookingState && !service) {
    return (
      <div className="booking-page">
        <div className="container">
          <h1>Service Not Found</h1>
          <p>The requested service could not be found. Please go back to the services page.</p>
        </div>
      </div>
    );
  }

  const handleBooking = () => {
    // Check authentication before proceeding with booking
    if (!authService.isAuthenticated()) {
      console.log('🔒 User not authenticated, redirecting to login');
      showBookingError('Please log in to book this service.');
      authService.login(window.location.pathname);
      return;
    }
    
    // Check for city mismatch error
    if (cityMismatchError) {
      showBookingError('Please resolve the address city mismatch before proceeding with booking.');
      return;
    }

    console.log('🎯 handleBooking called - groupBooking:', groupBooking);
    if (groupBooking) {
      handleGroupBooking();
    } else {
      handleSingleServiceBooking();
    }
  };

  const handleSingleServiceBooking = () => {
    const planForBooking = effectiveSelectedPlan;

    if (!selectedDate || !selectedTime) {
      showBookingError('Please select a date and time for the service.');
      return;
    }
    
    // Validate city match for selected address
    if (selectedAddress && !validateAddressCity(selectedAddress, selectedCity)) {
      const isDelhi = selectedCity?.code === 'DEL' || selectedCity?.name?.toLowerCase().includes('delhi');
      const message = isDelhi 
        ? `Selected address is in ${selectedAddress.cityName} but you're viewing prices for ${selectedCity?.name}. For Delhi pricing, please select an address in Delhi, Gurugram, Noida, Greater Noida, or Ghaziabad.`
        : `Selected address is in ${selectedAddress.cityName} but you're viewing prices for ${selectedCity?.name}. Please select an address in ${selectedCity?.name} or add a new address for the correct city.`;
      showBookingError(message);
      return;
    }
    
    if (!selectedAddress && !performSingleAtAntaryatra) {
      showBookingError('Please select an address or choose the Antaryatra site option.');
      return;
    }

    if (!currentService || !planForBooking) {
      showBookingError('Please select a service plan.');
      return;
    }

    // Proceed with single service booking logic (existing implementation)
    proceedToPayment();
  };

  const handleGroupBooking = () => {
    if (!groupBooking) return;
    
    // Validate all services are configured
    const unconfiguredServices = groupBooking.services.filter(service => !service.isConfigured);
    if (unconfiguredServices.length > 0) {
      showBookingError(`${unconfiguredServices.length} service(s) still need to be configured before proceeding.`);
      return;
    }
    
    // Proceed with group booking
    proceedToGroupPayment();
  };

  // Handle plan selection in group booking
  const handleGroupPlanSelection = (serviceIndex: number, plan: ServicePlan) => {
    updateServiceConfig(serviceIndex, { selectedPlan: plan });
    console.log(`📊 Plan changed for service ${serviceIndex}: ${plan.name} (₹${plan.price})`);
  };

  const proceedToGroupPayment = async () => {
    if (!groupBooking) return;
    
    // Check if booking hold is enabled
    if (bookingConfig.isBookingHoldEnabled) {
      // For group booking, capture interest for the first service as representative
      const firstService = groupBooking.services[0];
      if (firstService) {
        setPendingBookingData({
          serviceId: parseInt(firstService.cartItem.id.replace('service-', '')) || 1,
          serviceName: `${groupBooking.services.length} Services`,
          selectedPlan: firstService.selectedPlan,
          selectedDate: firstService.selectedDate,
          selectedTime: firstService.selectedTime,
          selectedAddress: useGlobalAddress ? globalSelectedAddress : firstService.selectedAddress,
          performAtAntaryatra: performAtAntaryatra,
          isGroupBooking: true
        });
        
        setShowBookingHoldModal(true);
        return;
      }
    }
    
    // Prepare service request data for payment page (don't create booking yet)
    const serviceRequestData = groupBooking.services.map(serviceConfig => {
      const address = performAtAntaryatra 
        ? null 
        : (useGlobalAddress ? globalSelectedAddress : serviceConfig.selectedAddress);
      
      if (!performAtAntaryatra && !address) {
        throw new Error('Address not selected for service');
      }
      
      return {
        serviceId: parseInt(serviceConfig.cartItem.id.replace('service-', '')) || 1,
        serviceName: serviceConfig.cartItem.name,
        selectedPlan: serviceConfig.selectedPlan,
        selectedDate: serviceConfig.selectedDate,
        selectedTime: serviceConfig.selectedTime,
        selectedAddress: address,
        duration: serviceConfig.selectedPlan?.duration || '60',
        priceEstimate: parseFloat(serviceConfig.selectedPlan?.price.replace(/[^0-9.-]+/g, '') || '0'),
        planName: serviceConfig.selectedPlan?.name,
        serviceImage: serviceConfig.cartItem.image,
        performAtAntaryatra: performAtAntaryatra
      };
    });
    
    const totalAmount = serviceRequestData.reduce((sum, req) => sum + req.priceEstimate, 0);
    
    // Navigate to payment page with group booking data (don't create booking yet)
    navigate('/payment', {
      state: {
        isGroupBooking: true,
        groupBookingId: `temp_${Date.now()}`, // Temporary ID, actual booking created in PaymentPage
        serviceRequests: serviceRequestData,
        totalAmount: totalAmount,
        services: groupBooking.services,
        useGlobalAddress,
        globalAddress: performAtAntaryatra ? null : globalSelectedAddress,
        performAtAntaryatra: performAtAntaryatra,
        cityCode: getCityCode()
      }
    });
  };

  const proceedToPayment = async () => {
    const planForBooking = effectiveSelectedPlan;

    console.log('🚀 proceedToPayment called with:', {
      selectedDate, selectedTime, selectedPlan: planForBooking, selectedAddress, currentService
    });
    
    if (!selectedDate || !selectedTime) {
      showBookingError('Please select a date and time for the service.');
      return;
    }

    if (!planForBooking) {
      showBookingError('Please select a service plan.');
      return;
    }

    if (!selectedAddress && !performSingleAtAntaryatra) {
      showBookingError('Please select an address or choose the Antaryatra site option.');
      return;
    }

    if (!currentService) {
      showBookingError('Service not found. Please refresh and try again.');
      return;
    }

    // Check if booking hold is enabled
    if (bookingConfig.isBookingHoldEnabled) {
      // Store booking data for later use
      setPendingBookingData({
        serviceId: typeof currentService.id === 'number' ? currentService.id : parseInt(String(currentService.id)) || 1,
        serviceName: currentService.name,
        selectedPlan: planForBooking,
        selectedDate,
        selectedTime,
        selectedAddress: performSingleAtAntaryatra ? null : selectedAddress,
        performAtAntaryatra: performSingleAtAntaryatra,
        serviceLocationType: (groupBooking ? performAtAntaryatra : performSingleAtAntaryatra) ? 'PLATFORM_SITE' : 'USER_SITE',
        platformSitePlan: (groupBooking ? performAtAntaryatra : performSingleAtAntaryatra) && platformSitePricing ? platformSitePricing.plans[0] : null
      });
      
      // Show booking hold modal instead of proceeding to payment
      setShowBookingHoldModal(true);
      return;
    }

    console.log('🎯 About to navigate to payment with state:', {
      serviceId: typeof currentService.id === 'number' ? currentService.id : parseInt(String(currentService.id)) || 1,
      serviceName: currentService.name,
      selectedPlan: planForBooking,
      selectedDate,
      selectedTime,
      selectedAddress: performSingleAtAntaryatra ? null : selectedAddress,
      duration: planForBooking.duration || '60',
      totalAmount: planForBooking.price,
      planName: planForBooking.name,
      serviceImage: currentService.image,
      isBookingPayment: true,
      performAtAntaryatra: performSingleAtAntaryatra
    });

    // Navigate to payment page with booking details (don't create booking yet)
    navigate('/payment', {
      state: {
        serviceId: typeof currentService.id === 'number' ? currentService.id : parseInt(String(currentService.id)) || 1,
        serviceName: currentService.name,
        selectedPlan: planForBooking,
        selectedDate,
        selectedTime,
        selectedAddress: performSingleAtAntaryatra ? null : selectedAddress,
        duration: planForBooking.duration || '60',
        totalAmount: getTotalAmountForPayment().toString(), // Use total amount with GST for payment
        baseAmount: (groupBooking ? performAtAntaryatra : performSingleAtAntaryatra) && platformSitePricing && planForBooking
          ? (() => {
              const matchingPlatformPlan = platformSitePricing.plans.find(platformPlan => {
                const planCode = planForBooking.name.toLowerCase().replace(/[^a-z]/g, '');
                const platformPlanCode = platformPlan.planCode.toLowerCase();
                return platformPlanCode.includes(planCode) || planCode.includes(platformPlanCode);
              });
              return matchingPlatformPlan ? Math.round(matchingPlatformPlan.totalAmount).toString() : planForBooking.price;
            })()
          : planForBooking.price, // Store base price separately
        planName: planForBooking.name,
        serviceImage: currentService.image,
        isBookingPayment: true,
        performAtAntaryatra: performSingleAtAntaryatra,
        planCode: getPlanCode(planForBooking.name),
        cityCode: getCityCode(),
        serviceLocationType: (groupBooking ? performAtAntaryatra : performSingleAtAntaryatra) ? 'PLATFORM_SITE' : 'USER_SITE'
      }
    });
  };

  const handleAddToCart = () => {
    const planForBooking = effectiveSelectedPlan;
    if (!currentService || !planForBooking) return;
    
    const cartItem = {
      id: `${currentService.id}-${planForBooking.id}`,
      name: currentService.name,
      price: planForBooking.price,
      image: currentService.image,
      category: currentMainService?.name || 'Puja Services',
      duration: planForBooking.duration,
      planId: planForBooking.id.toString(),
      planName: planForBooking.name
    };
    
    addToCart(cartItem);
    showToast('success', 'Added to cart!');
  };

  const handleAddToWishlist = () => {
    const planForBooking = effectiveSelectedPlan;
    if (!currentService || !planForBooking) return;
    
    const wishlistItem = {
      id: currentService.id.toString(),
      name: currentService.name,
      price: planForBooking.price,
      image: currentService.image,
      category: currentMainService?.name || 'Puja Services',
      duration: planForBooking.duration
    };
    
    addToWishlist(wishlistItem);
    setIsInUserWishlist(true);
    showToast('success', 'Added to wishlist! ♥');
  };

  const openPlanModal = (plan: ExtendedServicePlan) => {
    setPlanModalService(plan);
    setPlanModalOpen(true);
  };

  const closePlanModal = () => {
    setPlanModalOpen(false);
    setPlanModalService(null);
  };

  return (
    <div className="booking-page">
      {groupBooking ? (
        /* Multi-service booking layout */
        <section className="group-booking-section">
          <div className="container">
            
            <div className="group-booking-header-cart">
              <h1>Configure Your Services</h1>
              <p>You have {groupBooking.services.length} services to configure. Please set plans, dates, and times for each service.</p>
              <div className="group-booking-summary-cart">
                <span className="service-count">{groupBooking.services.length} Services</span>
                <span className="total-amount">Total (incl. GST): ₹{groupBooking.totalAmount}</span>
                {/* <span className="group-id">Group ID: {groupBooking.groupBookingId}</span> */}
              </div>
            </div>

            {/* Global Address Selection */}
            <div className="global-address-section">
              <div className="address-toggle">
                <label>
                  <input
                    type="checkbox"
                    checked={useGlobalAddress}
                    onChange={(e) => setUseGlobalAddress(e.target.checked)}
                  />
                  Use same address for all services
                </label>
              </div>
              
              {/* Conditionally show platform site option for group booking */}
              {groupBooking && groupBooking.services.some(serviceConfig => {
                // Find original service data to check platformSitePreferred
                const findOriginalService = (cartItemName: string) => {
                  for (const [category, services] of Object.entries(subServicesData)) {
                    const service = services.find(s => s.name === cartItemName);
                    if (service) return service;
                  }
                  return null;
                };
                const originalService = findOriginalService(serviceConfig.cartItem.name);
                return originalService?.platformSitePreferred === true;
              }) && (
                <div className="antaryatra-option">
                  <label>
                    <input
                      type="checkbox"
                      checked={performAtAntaryatra}
                      onChange={async (e) => {
                        setPerformAtAntaryatra(e.target.checked);
                        if (e.target.checked) {
                          setGlobalSelectedAddress(null);
                          setUseGlobalAddress(true); // Enable global setting for consistency
                          
                          // Trigger platform pricing for current service
                          if (currentService && selectedPlan) {
                            const serviceId = typeof currentService.id === 'number' ? currentService.id : parseInt(String(currentService.id)) || 1;
                            await handlePlatformSitePricing(serviceId, selectedPlan);
                          }
                        }
                      }}
                    />
                    Perform Pooja at Antaryatra site Varanasi
                  </label>
                  {/* Show platform site message if available */}
                  {performAtAntaryatra && groupBooking.services.some(serviceConfig => {
                    const findOriginalService = (cartItemName: string) => {
                      for (const [category, services] of Object.entries(subServicesData)) {
                        const service = services.find(s => s.name === cartItemName);
                        if (service) return service;
                      }
                      return null;
                    };
                    const originalService = findOriginalService(serviceConfig.cartItem.name);
                    return originalService?.platformSiteMessage;
                  }) && (
                    <div className="platform-site-message">
                      <p className="info-message">
                        ⓘ {(() => {
                          // Find first service with platform site message
                          for (const serviceConfig of groupBooking.services) {
                            for (const [, services] of Object.entries(subServicesData)) {
                              const service = services.find(s => s.name === serviceConfig.cartItem.name);
                              if (service?.platformSiteMessage) {
                                return service.platformSiteMessage;
                              }
                            }
                          }
                          return 'This service is more effective at our dedicated temple site.';
                        })()}
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Address Selection - Only show for authenticated users */}
              {isAuthenticated && (
                <>
                  {useGlobalAddress && !performAtAntaryatra && (
                    <div className="global-address-selector">
                      <h3>Select Address for All Services</h3>
                      
                      {/* City Mismatch Warning */}
                      {cityMismatchError && (
                        <div className="city-mismatch-warning">
                          <div className="warning-content">
                            <span className="warning-icon">⚠️</span>
                            <p>{cityMismatchError}</p>
                          </div>
                        </div>
                      )}
                      
                      {userAddresses.length > 0 ? (
                        <div className="address-grid">
                          {userAddresses.map((address) => (
                            <div
                              key={address.id}
                              className={`address-card ${
                                globalSelectedAddress?.id === address.id ? 'selected' : ''
                              } ${
                                !validateAddressCity(address, selectedCity) ? 'city-mismatch' : ''
                              }`}
                              onClick={() => {
                                setGlobalSelectedAddress(address);
                                if (!validateAddressCity(address, selectedCity)) {
                                  const isDelhi = selectedCity?.code === 'DEL' || selectedCity?.name?.toLowerCase().includes('delhi');
                                  const message = isDelhi 
                                    ? `Selected address is in ${address.cityName} but you're viewing prices for ${selectedCity?.name}. For Delhi pricing, please select an address in Delhi, Gurugram, Noida, Greater Noida, or Ghaziabad.`
                                    : `Selected address is in ${address.cityName} but you're viewing prices for ${selectedCity?.name}. Please select an address in ${selectedCity?.name} or add a new address for the correct city.`;
                                  setCityMismatchError(message);
                                } else {
                                  setCityMismatchError('');
                                }
                              }}
                            >
                              <div className="address-info">
                                <h4>{address.name}</h4>
                                <p>
                                  {address.line1}
                                  {address.line2 && `, ${address.line2}`}
                                </p>
                                <p>
                                  {address.cityName || address.city}, {address.stateName || address.state} - {address.postalCode}
                                </p>
                                {!validateAddressCity(address, selectedCity) && (
                                  <div className="city-mismatch-badge">
                                    <span>⚠️ Different City</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p>No addresses found. Please add an address first.</p>
                      )}
                    </div>
                  )}
                </>
              )}
              
              {/* Non-authenticated user message */}
              {!isAuthenticated && !performAtAntaryatra && (
                <div className="auth-required-message">
                  <div className="auth-message-content">
                    <span className="auth-icon">🔐</span>
                    <p>Please log in to select a service address or choose the Antaryatra site option.</p>
                  </div>
                </div>
              )}
              
              {performAtAntaryatra && (
                <div className="antaryatra-message">
                  <div className="antaryatra-location-card">
                    <h3>🕉️ Service Location</h3>
                    <p><strong>Antaryatra Temple Site</strong></p>
                    <p>📍 Varanasi, Uttar Pradesh</p>
                    <p>✨ Sacred ritual will be performed at our dedicated temple site</p>
                  </div>
                </div>
              )}
            </div>

            {/* Services Accordion */}
            <div className="services-accordion">
              {groupBooking.services.map((serviceConfig, index) => (
                <ServiceAccordionItem 
                  key={index}
                  serviceConfig={serviceConfig}
                  index={index}
                  isActive={activeServiceIndex === index}
                  userAddresses={userAddresses}
                  useGlobalAddress={useGlobalAddress}
                  globalSelectedAddress={globalSelectedAddress}
                  updateServiceConfig={updateServiceConfig}
                  onToggle={() => handleAccordionToggle(index)}
                  availablePlans={(serviceConfig as any).availablePlans || []} // Pass dynamic plans with backend pricing
                  performAtAntaryatra={performAtAntaryatra}
                  isPlatformPricingConfirmed={isPlatformPricingConfirmed}
                  onPlatformPricing={handlePlatformSitePricing}
                  onShowPlatformModal={(serviceId: number, selectedPlan: any) => {
                    setTempPlatformPricing({ serviceId, selectedPlan });
                    setShowPlatformPriceModal(true);
                  }}
                />
              ))}
            </div>

            {/* Group Booking Actions */}
            <div className="group-booking-actions">
              <div className="booking-summary">
                <p>Configured: {groupBooking.services.filter(s => s.isConfigured).length}/{groupBooking.services.length} services</p>
                <p className="total-amount-cart">Total (incl. GST): ₹{groupBooking.totalAmount}</p>
              </div>
              <button 
                className={`group-booking-btn ${groupBooking.services.every(s => s.isConfigured) ? 'ready' : 'disabled'}`}
                onClick={handleGroupBooking}
                disabled={!groupBooking.services.every(s => s.isConfigured)}
              >
                {groupBooking.services.every(s => s.isConfigured) ? 'Proceed to Payment' : 'Configure All Services'}
              </button>
            </div>
          </div>
        </section>
      ) : (
        <>
      {/* Breadcrumb */}
      <nav className="booking-breadcrumb" aria-label="Breadcrumb">
        <div className="container">
          <Link to="/">Home</Link>
          <span className="breadcrumb-sep">›</span>
          <Link to={`/service/${serviceCategory}`}>{currentMainService?.name || serviceCategory}</Link>
          <span className="breadcrumb-sep">›</span>
          <span className="breadcrumb-current">{currentService.name}</span>
        </div>
      </nav>

      {/* Context ribbon — echoes landing card intent */}
      {currentService.bestFor && currentService.bestFor.length > 0 && (
        <div className="booking-context-ribbon">
          <div className="container">
            <span className="context-ribbon-label">Best for:</span>
            {currentService.bestFor.slice(0, 3).map((item, i) => (
              <span key={i} className="context-badge">{item}</span>
            ))}
          </div>
        </div>
      )}

      {/* Service Header with Booking Form */}
      <section className="service-header">
        <div className="container">
          
          <div className="service-header-content">{/* Left Side - Image and Basic Info */}
            <div className="service-left">
              <div className="service-image-container">
                <img src={currentService.image} alt={currentService.name} className="service-main-image" />
              </div>
              
              <div className="service-basic-info">
                <h3>{currentService.name}</h3>
                {/* <div className="service-rating">
                  <div className="stars">
                    {[...Array(5)].map((_, index) => (
                      <svg
                        key={index}
                        className={`star ${index < currentService.rating ? 'filled' : ''}`}
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                    ))}
                  </div>
                  <span className="rating-text">{currentService.rating}.0 ({reviews.length} reviews)</span>
                </div> */}
                <p className="service-description">{currentService.description}</p>

                {showServiceTrustSection && (
                  <div className="service-trust-section" aria-label="Why choose this puja">
                    {serviceTrustBadgeText && (
                      <div className="service-trust-badge">{serviceTrustBadgeText}</div>
                    )}
                    {serviceTrustSummaryBadges.length > 0 && (
                      <div className="service-trust-summary">
                        {serviceTrustSummaryBadges.map((badge) => (
                          <div key={badge} className="service-trust-pill">{badge}</div>
                        ))}
                      </div>
                    )}
                    <div className="service-why-book-card">
                      <h3>Why book with Antaryatra?</h3>
                      <ul>
                        {serviceWhyBookPoints.map((point, index) => {
                          if (typeof point === 'string') {
                            return <li key={`${point}-${index}`}>{point}</li>;
                          }

                          return (
                            <li key={`${point.title}-${index}`}>
                              <span>{point.title}</span>
                              <span>{point.description}</span>
                            </li>
                          );
                        })}
                      </ul>
                      {/* <div className="service-guidance-actions">
                        <button
                          type="button"
                          className="service-guidance-btn"
                          onClick={() => navigate('/contact', { state: { serviceName: currentService.name, source: 'booking-page' } })}
                        >
                          Request a callback
                        </button>
                      </div> */}
                    </div>
                  </div>
                )}
                
                {/* What's Included */}
                {benefits.length > 0 && (
                  <div className="service-benefits">
                    <h3>Importance</h3>
                    <ul className="benefits-list">
                      {benefits.map((benefit) => (
                        <li key={benefit.id} className="benefit-item">
                          <span className="benefit-icon">{benefit.icon}</span>
                          <div className="benefit-content">
                            <strong>{benefit.title}</strong>
                            <p>{benefit.description}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Right Side - Booking Form */}
            <div className="service-right">
              <div className="booking-card">
                {/* <div className="price-section">
                  <div className="current-price">
                    <span className="price-label">From</span>
                    {isLoadingPricing ? (
                      <span className="price-value price-loading" aria-label="Loading price">
                        <span className="price-skeleton"></span>
                      </span>
                    ) : (
                      <span className="price-value">{fromPrice}</span>
                    )}
                    {!isLoadingPricing && defaultDisplayPlan?.originalPrice && (
                      <span className="original-price">{defaultDisplayPlan.originalPrice}</span>
                    )}
                  </div>
                  {defaultDisplayPlan?.popular && (
                    <span className="popular-badge">Most Popular</span>
                  )}
                </div> */}

                {/* Price clarity — booking logistics, not duplicated in plan cards */}
                {/* {!isLoadingPricing && (currentService.minAdvanceBooking || currentService.cancellationPolicy) && (
                  <p className="price-clarity-note">
                    {currentService.minAdvanceBooking && (
                      <span>🕐 Book {currentService.minAdvanceBooking} in advance</span>
                    )}
                    {currentService.minAdvanceBooking && currentService.cancellationPolicy && (
                      <span className="clarity-sep"> · </span>
                    )}
                    {currentService.cancellationPolicy && (
                      <span>✓ Cancel free: {currentService.cancellationPolicy}</span>
                    )}
                  </p>
                )} */}

                {currentService?.platformSitePreferred && (
                  <div className="service-location-choice">
                    <div className="service-location-choice-header">
                      <h4>Where will your puja be performed?</h4>
                      <p>Choose the place that suits your preference.</p>
                    </div>
                    <div
                      className={`antaryatra-premium-card${performSingleAtAntaryatra ? ' selected' : ''}${currentService?.siteOnly ? ' site-only-locked' : ''}`}
                      onClick={async () => {
                        if (currentService?.siteOnly) {
                          showToast('info', currentService.siteOnlyMessage || 'This puja can only be performed at our designated Kashi site and cannot be booked for home delivery.');
                          return;
                        }
                        const next = !performSingleAtAntaryatra;
                        setPerformSingleAtAntaryatra(next);
                        if (next) {
                          setSelectedAddress(null);
                          if (currentService && effectiveSelectedPlan) {
                            const serviceId = typeof currentService.id === 'number' ? currentService.id : parseInt(String(currentService.id)) || 1;
                            await handlePlatformSitePricing(serviceId, effectiveSelectedPlan);
                          }
                        }
                      }}
                      role="checkbox"
                      aria-checked={performSingleAtAntaryatra}
                      aria-disabled={currentService?.siteOnly}
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') e.currentTarget.click(); }}
                    >
                      <div className="antaryatra-card-header">
                        <div className="antaryatra-location-option">
                          <span className={`antaryatra-location-label${!performSingleAtAntaryatra ? ' active' : ''}`}>Home</span>
                          <span className="antaryatra-location-subtext">Delhi NCR</span>
                        </div>
                        <div className={`antaryatra-toggle${performSingleAtAntaryatra ? ' on' : ''}`}>
                          <span className="antaryatra-toggle-knob" />
                        </div>
                        <div className="antaryatra-location-option">
                          <span className={`antaryatra-location-label${performSingleAtAntaryatra ? ' active' : ''}`}>Antaryatra Site</span>
                          <span className="antaryatra-location-subtext">Varanasi</span>
                        </div>
                      </div>
                      <div className="antaryatra-card-benefits">
                        <span>✓ Kashi-lineage Acharyas</span>
                        <span>✓ Video of the ritual included</span>
                        <span>✓ Prasad courier to you</span>
                        <span>✓ Sacred site/Ghats &amp; samagri</span>
                      </div>
                      {currentService?.siteOnly && (
                        <p className="antaryatra-site-only-notice">🏛️ {currentService.siteOnlyMessage || 'This puja is performed exclusively at our sacred Kashi site and is not available for home booking.'}</p>
                      )}
                      {performSingleAtAntaryatra && currentService?.platformSiteMessage && (
                        <p className="antaryatra-card-note">ℹ️ {currentService.platformSiteMessage}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Plan Selection */}
                {plans.length > 0 && (
                  <div className="plan-selection">
                    <h3>Choose Pooja Plan that suits your needs</h3>

                    {/* Ritual steps preview */}
                    {/* {(() => {
                      const steps = selectedPlan?.ritualSteps || plans[0]?.ritualSteps || [];
                      return steps.length > 0 ? (
                        <div className="ritual-steps-preview">
                          {steps.slice(0, 3).map((step, i) => (
                            <span key={i} className="ritual-step-pill">
                              <span className="ritual-step-num">{i + 1}</span>
                              {step}
                            </span>
                          ))}
                          {steps.length > 3 && (
                            <span className="ritual-step-more">+{steps.length - 3} more steps</span>
                          )}
                        </div>
                      ) : null;
                    })()} */}

                    <div className="plans-list">
                      {plans.filter((plan) => !(plan as any).hidden).map((plan) => {
                        // Get the current selected plan for single service only
                        const currentSelectedPlan = effectiveSelectedPlan;
                        
                        // Get display price based on Antaryatra selection
                        const getDisplayPrice = (plan: any) => {
                          const isAntaryatraSelected = performSingleAtAntaryatra;
                          if (isAntaryatraSelected && platformSitePricing) {
                            // Find matching platform plan for this plan
                            const matchingPlatformPlan = platformSitePricing.plans.find(platformPlan => {
                              const planCode = plan.name.toLowerCase().replace(/[^a-z]/g, '');
                              const platformPlanCode = platformPlan.planCode.toLowerCase();
                              return platformPlanCode.includes(planCode) || planCode.includes(platformPlanCode);
                            });
                            return matchingPlatformPlan ? `₹${Math.round(matchingPlatformPlan.totalAmount).toLocaleString()}` : plan.price;
                          }
                          return plan.price;
                        };

                        return (
                        <div 
                          key={`${plan.id}-${performSingleAtAntaryatra ? 'platform' : 'user'}`}
                          className={`plan-option plan-tier-${(plan as any).tierLevel ?? plan.id} ${currentSelectedPlan?.id === plan.id ? 'selected' : ''}`}
                          onClick={async () => {
                            setSelectedPlan(plan);
                            setBookingError(''); // clear any validation error
                            // If Antaryatra is selected, update platform pricing for new plan
                            if (performSingleAtAntaryatra && currentService) {
                              const serviceId = typeof currentService.id === 'number' ? currentService.id : parseInt(String(currentService.id)) || 1;
                              if (isPlatformPricingConfirmed) {
                                await handlePlatformSitePricing(serviceId, plan);
                              } else {
                                setTempPlatformPricing({ serviceId, selectedPlan: plan });
                                setShowPlatformPriceModal(true);
                              }
                            }
                          }}
                        >
                          <div className="plan-header">
                            <div className="plan-title-section">
                              <span className="plan-name">{plan.name}</span>
                              {(plan.planTag || plan.popular) && (
                                <span className="popular-tag">{plan.planTag || 'Most Popular'}</span>
                              )}
                              {/* {performSingleAtAntaryatra && platformSitePricing && (
                                <span className="platform-price-tag">📍 At Kashi</span>
                              )} */}
                            </div>
                            {isLoadingPricing ? (
                              <span className="plan-price"><span className="price-skeleton plan-price-skeleton"></span></span>
                            ) : (
                              <span className="plan-price">{getDisplayPrice(plan)}</span>
                            )}
                          </div>
                          
                          <div className="plan-details">
                            <p className="plan-duration">⏱ {plan.duration}
                              {plan.priests ? <span className="plan-priests"> · {plan.priests} Acharya{plan.priests > 1 ? 's' : ''}</span> : null}
                            </p>

                            {/* Jaap count badge — most prominent differentiator */}
                            {plan.jaapCount && (
                              <div className="plan-jaap-badge"> {plan.jaapCount}</div>
                            )}

                            {/* Highlight tags — switch between site and home tags based on toggle */}
                            {(() => {
                              const tags = performSingleAtAntaryatra
                                ? (plan.siteHighlightTags ?? plan.highlightTags)
                                : plan.highlightTags;
                              return tags && tags.length > 0 ? (
                                <div className="plan-highlight-tags">
                                  {tags.map((tag, i) => (
                                    <span key={i} className="plan-tag">{tag}</span>
                                  ))}
                                </div>
                              ) : null;
                            })()
                            }

                            {/* Fallback: generic highlights when no tags provided */}
                            {(() => {
                              const tags = performSingleAtAntaryatra
                                ? (plan.siteHighlightTags ?? plan.highlightTags)
                                : plan.highlightTags;
                              if (tags && tags.length > 0) return null;
                              return (
                              <div className="plan-highlights">
                                {plan.priests && (
                                  <div className="highlight-item">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A1.998 1.998 0 0 0 18 7c-.8 0-1.54.5-1.85 1.26l-1.92 5.63c-.18.53.18 1.11.74 1.11H16v7h4zM12.5 11.5c.83 0 1.5-.67 1.5-1.5s-.67-1.5-1.5-1.5S11 9.17 11 10s.67 1.5 1.5 1.5zm1.5 1h-4c-1.1 0-2 .9-2 2v7h2v-5h4v5h2v-7c0-1.1-.9-2-2-2z"/>
                                    </svg>
                                    <span>{plan.priests} Acharya{plan.priests > 1 ? 's' : ''}</span>
                                  </div>
                                )}
                                {plan.ritualSteps && plan.ritualSteps.length > 0 && (
                                  <div className="highlight-item">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                                    </svg>
                                    <span>{plan.ritualSteps.length} Ritual Steps</span>
                                  </div>
                                )}
                              </div>
                              );
                            })()}

                            {/* <button
                              className="view-plan-details-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                openPlanModal(plan);
                              }}
                            >
                              View full details ›
                            </button> */}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Booking Form */}
                <div className="booking-form">
                  {/* Address Selection */}
                  <div className="form-section">
                    <h3>Service Address</h3>
                    
                    {performSingleAtAntaryatra && (
                      <div className="antaryatra-single-message">
                        <div className="antaryatra-location-card">
                          <h4>🕉️ Service Location</h4>
                          <p><strong>Antaryatra Site</strong></p>
                          <p>📍 Varanasi, Uttar Pradesh</p>
                          <p>✨ Sacred ritual will be performed at our dedicated temple site</p>
                        </div>
                      </div>
                    )}
                    
                    {!performSingleAtAntaryatra && !currentService?.siteOnly && (
                      <>
                        {isLoadingAddresses ? (
                          <div className="address-loading" style={{ 
                            padding: '2rem', 
                            textAlign: 'center', 
                            backgroundColor: '#f8f9fa', 
                            borderRadius: '8px',
                            border: '1px solid #dee2e6'
                          }}>
                            <p>🔄 Loading addresses...</p>
                          </div>
                        ) : userAddresses.length > 0 ? (
                      <div className="address-selection">
                        {userAddresses.map((address) => (
                          <label 
                            key={address.id} 
                            className={`address-option ${selectedAddress?.id === address.id ? 'selected' : ''}`}
                          >
                            <input
                              type="radio"
                              name="address"
                              value={address.id}
                              checked={selectedAddress?.id === address.id}
                              onChange={() => setSelectedAddress(address)}
                            />
                            <div className="address-info">
                              <div className="address-header">
                                <span className={`address-type ${address.addressType || address.type}`}>
                                  {address.label || address.name}
                                </span>
                                {(address.isDefault || address.default) && <span className="default-tag">Default</span>}
                              </div>
                              <p className="address-text">
                                {address.line1 || address.addressLine1}
                                {(address.line2 || address.addressLine2) && `, ${address.line2 || address.addressLine2}`}
                                {address.landmark && `, ${address.landmark}`}
                                {address.cityName && `, ${address.cityName}`}
                                {address.stateName && `, ${address.stateName}`}
                                {!address.cityName && `, City ID: ${address.city}`}
                                {!address.stateName && `, State ID: ${address.state}`}
                                {` - ${address.postalCode || address.pincode}`}
                              </p>
                            </div>
                          </label>
                        ))}
                        <Link to="/profile" className="add-address-link">
                          + Add New Address
                        </Link>
                      </div>
                    ) : (
                      <div className="no-address">
                        <p>No saved addresses found</p>
                        <Link to="/profile" className="add-address-btn">
                          Where Pooja will be performed?
                        </Link>
                      </div>
                    )}
                      </>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="date">Select Date</label>
                    <input
                      type="date"
                      id="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      min={getMinBookingDate()}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="time">Preferred Time (Indicative)</label>
                    {/* <span className="feature">Free cancellation until payment(as per cancellation policy)</span> */}
                    <select
                      id="time"
                      value={selectedTime}
                      onChange={(e) => setSelectedTime(e.target.value)}
                    >
                      <option value="">Choose time</option>
                      <option value="09:00">9:00 AM</option>
                      <option value="10:00">10:00 AM</option>
                      <option value="11:00">11:00 AM</option>
                      <option value="14:00">2:00 PM</option>
                      <option value="15:00">3:00 PM</option>
                      <option value="16:00">4:00 PM</option>
                    </select>
                  </div>
                  
                  <div className="booking-trust-strip">
                    <span>✓ Kashi-lineage Acharya</span>
                    <span className="trust-dot">·</span>
                    <span>✓ All samagri included</span>
                    <span className="trust-dot">·</span>
                    <span>✓ Easy Rescheduling & cancellation</span>
                  </div>

                  <div className="booking-actions">
                    {bookingError && (
                      <div ref={errorBannerRef} className="booking-inline-error" role="alert" tabIndex={-1}>
                        <span>⚠️ {bookingError}</span>
                        <button className="booking-inline-error-close" onClick={() => setBookingError('')} aria-label="Dismiss">×</button>
                      </div>
                    )}
                    <div className="primary-action">
                      <button 
                        className="book-cta-btn primary" 
                        onClick={handleBooking}
                      >
                        Book Your Pooja - {getBaseAmountForDisplay()}
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd"/>
                        </svg>
                      </button>
                    </div>

                    <div className="booking-security-badges">
                      <div className="booking-security-item">
                        <span className="booking-security-icon">🔒</span>
                        <span>SSL Encrypted Payment</span>
                      </div>
                      <div className="booking-security-item">
                        <span className="booking-security-icon">🛡️</span>
                        <span>100% Secure Transaction (Razorpay)</span>
                      </div>
                    </div>
                    
                    {/* <div className="secondary-actions">
                      <button className="add-to-cart-btn" onClick={handleAddToCart}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M7 18C5.9 18 5.01 18.9 5.01 20S5.9 22 7 22 8.99 21.1 8.99 20 8.1 18 7 18ZM1 2V4H3L6.6 11.59L5.24 14.04C5.09 14.32 5 14.65 5 15C5 16.1 5.9 17 7 17H19V15H7.42C7.28 15 7.17 14.89 7.17 14.75L7.2 14.63L8.1 13H15.55C16.3 13 16.96 12.58 17.3 11.97L20.88 5H5.21L4.27 3H1V2H1ZM17 18C15.9 18 15.01 18.9 15.01 20S15.9 22 17 22 18.99 21.1 18.99 20 18.1 18 17 18Z" fill="currentColor"/>
                        </svg>
                        Add to Cart
                      </button>
                      
                      <button 
                        className={`add-to-wishlist-btn ${isInUserWishlist ? 'in-wishlist' : ''}`} 
                        onClick={handleAddToWishlist}
                        disabled={isInUserWishlist}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill={isInUserWishlist ? "currentColor" : "none"} xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 21.35L10.55 20.03C5.4 15.36 2 12.28 2 8.5C2 5.42 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.09C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.42 22 8.5C22 12.28 18.6 15.36 13.45 20.04L12 21.35Z" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        {isInUserWishlist ? 'In Wishlist' : 'Add to Wishlist'}
                      </button>
                    </div> */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* Promotions & Offers */}
      {/* {promotions.length > 0 && (
        <section className="promotions-section">
          <div className="container">
            <h2>Special Offers</h2>
            <div className="promotions-grid">
              {promotions.map((promotion, index) => (
                <div key={index} className="promotion-card">
                  <div className="promotion-icon">🎉</div>
                  <h3>{promotion.title}</h3>
                  <p>{promotion.description}</p>
                  <span className="promo-code">Code: {promotion.code}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )} */}



      {/* Customer Testimonials */}
      {/* <section className="testimonials-section">
        <div className="container">
          <h2>What Our Customers Say</h2>
          <div className="testimonials-grid">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="testimonial-card">
                <div className="testimonial-content">
                  <p>"{testimonial.testimonial}"</p>
                </div>
                <div className="testimonial-author">
                  <img src={testimonial.photo} alt={testimonial.customerName} />
                  <div className="author-info">
                    <h4>{testimonial.customerName}</h4>
                    <span>{testimonial.service}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Reviews Section */}
      {/* <section className="reviews-section">
        <div className="container">
          <h2>Customer Reviews</h2>
          <div className="reviews-grid">
            {reviews.map((review) => (
              <div key={review.id} className="review-card">
                <div className="review-header">
                  <div className="reviewer-info">
                    <img src={review.avatar} alt={review.customerName} />
                    <div>
                      <h4>{review.customerName}</h4>
                      <span className="review-date">{review.date}</span>
                    </div>
                  </div>
                  <div className="review-rating">
                    {[...Array(5)].map((_, index) => (
                      <svg
                        key={index}
                        className={`star ${index < review.rating ? 'filled' : ''}`}
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                    ))}
                  </div>
                </div>
                <p className="review-comment">{review.comment}</p>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Recommended Services */}
      {/* <section className="recommended-section">
        <div className="container">
          <h2>You Might Also Like</h2>
          <div className="recommended-grid">
            {recommendedServices.map((service) => (
              <div key={service.id} className="recommended-card">
                {service.trending && (
                  <div className="trending-badge">🔥 Trending</div>
                )}
                <img src={service.image} alt={service.name} />
                <div className="recommended-info">
                  <h3>{service.name}</h3>
                  <div className="recommended-meta">
                    <span className="recommended-price">{service.price}</span>
                    <div className="recommended-rating">
                      <svg className="star filled" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                      <span>{service.rating}</span>
                    </div>
                  </div>
                  <button className="recommended-btn">View Details</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Plan Details Modal */}
      {planModalOpen && planModalService && (
        <div className="modal-overlay" onClick={closePlanModal}>
          <div className="plan-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{planModalService.name}</h2>
              <button className="close-modal-btn" onClick={closePlanModal}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
            
            <div className="modal-content">
              <div className="plan-summary">
                <div className="plan-pricing">
                  <span className="price">{planModalService.price}</span>
                  {planModalService.originalPrice && (
                    <span className="original-price">{planModalService.originalPrice}</span>
                  )}
                  <span className="duration">{planModalService.duration}</span>
                </div>
                {(planModalService.planTag || planModalService.popular) && (
                  <span className="popular-badge">{planModalService.planTag || 'Most Popular Choice'}</span>
                )}
              </div>

              <div className="modal-grid">
                <div className="modal-left">
                  {planModalService.whatThisFeelsLike && planModalService.whatThisFeelsLike.length > 0 && (
                    <div className="modal-section">
                      <h3>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 3c.55 0 1 .45 1 1v7h2c.55 0 1 .45 1 1v8c0 .55-.45 1-1 1h-6c-.55 0-1-.45-1-1v-8c0-.55.45-1 1-1h2V4c0-.55.45-1 1-1zm-8 9c.55 0 1 .45 1 1v7c0 .55-.45 1-1 1s-1-.45-1-1v-7c0-.55.45-1 1-1zm16 0c.55 0 1 .45 1 1v7c0 .55-.45 1-1 1s-1-.45-1-1v-7c0-.55.45-1 1-1zm-8-3c-1.1 0-2 .9-2 2v1c0 .55.45 1 1 1h2c.55 0 1-.45 1-1v-1c0-1.1-.9-2-2-2z"/>
                        </svg>
                        What This Feels Like
                        
                      </h3>
                      <ul className="includes-list">
                        {planModalService.whatThisFeelsLike.map((feeling, index) => (
                          <li key={index}>{feeling}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {planModalService.priests && (
                    <div className="modal-section">
                      <h3>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A1.998 1.998 0 0 0 18 7c-.8 0-1.54.5-1.85 1.26l-1.92 5.63c-.18.53.18 1.11.74 1.11H16v7h4zM12.5 11.5c.83 0 1.5-.67 1.5-1.5s-.67-1.5-1.5-1.5S11 9.17 11 10s.67 1.5 1.5 1.5zm1.5 1h-4c-1.1 0-2 .9-2 2v7h2v-5h4v5h2v-7c0-1.1-.9-2-2-2z"/>
                        </svg>
                        Acharya Ji & Expertise
                      </h3>
                      <p><strong>{planModalService.priests} qualified priest{planModalService.priests > 1 ? 's' : ''}</strong> will conduct your ceremony with traditional Sanskrit recitations and proper Vedic rituals.</p>
                    </div>
                  )}

                  {planModalService.materials && planModalService.materials.length > 0 && (
                    <div className="modal-section">
                      <h3>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                        </svg>
                        Materials Provided
                      </h3>
                      <ul className="materials-list">
                        {planModalService.materials.map((material, index) => (
                          <li key={index}>{material}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {planModalService.includes && planModalService.includes.length > 0 && (
                    <div className="modal-section">
                      <h3>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/>
                        </svg>
                        What's Included
                      </h3>
                      <ul className="includes-list">
                        {planModalService.includes.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="modal-right">
                  {planModalService.ritualSteps && planModalService.ritualSteps.length > 0 && (
                    <div className="modal-section">
                      <h3>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                        </svg>
                        Ceremony Steps
                      </h3>
                      <ol className="ritual-steps-list">
                        {planModalService.ritualSteps.map((step, index) => (
                          <li key={index}>{step}</li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {planModalService.bestFor && planModalService.bestFor.length > 0 && (
                    <div className="modal-section">
                      <h3>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                        </svg>
                        Best For
                      </h3>
                      <ul className="best-for-list">
                        {planModalService.bestFor.map((purpose, index) => (
                          <li key={index}>{purpose}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {planModalService.languages && planModalService.languages.length > 0 && (
                    <div className="modal-section">
                      <h3>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12.65 15.67c.14-.36.05-.77-.23-1.05l-2.09-2.09.03-.03c1.74-1.94 2.98-4.17 3.71-6.53h1.94c.54 0 .99-.45.99-.99v-.02c0-.54-.45-.99-.99-.99H10V3c0-.55-.45-1-1-1s-1 .45-1 1v1H2.99c-.54 0-.99.45-.99.99 0 .55.45 1 .99 1h8.3c-.65 1.97-1.69 3.85-3.06 5.46-.56-.62-1.03-1.28-1.41-1.99-.27-.50-.85-.77-1.35-.51-.5.27-.77.85-.51 1.35.48.9 1.08 1.75 1.78 2.52-.69.61-1.42 1.19-2.16 1.73-.54.4-.65 1.15-.24 1.68.23.3.58.47.96.47.25 0 .5-.09.69-.26.85-.63 1.64-1.32 2.37-2.05L7.85 14.5c-.28.28-.37.69-.23 1.05.14.36.49.6.88.6h5c.39 0 .74-.24.88-.6z"/>
                        </svg>
                        Languages Available
                      </h3>
                      <p>{planModalService.languages.join(', ')}</p>
                    </div>
                  )}

                  {planModalService.specialFeatures && planModalService.specialFeatures.length > 0 && (
                    <div className="modal-section">
                      <h3>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                        </svg>
                        Special Features
                      </h3>
                      <ul className="special-features-list">
                        {planModalService.specialFeatures.map((feature, index) => (
                          <li key={index}>{feature}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <div className="booking-info">
                  {planModalService.minAdvanceBooking && (
                    <p className="advance-booking">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                      </svg>
                      Book {planModalService.minAdvanceBooking} in advance
                    </p>
                  )}
                  {planModalService.cancellationPolicy && (
                    <p className="cancellation-policy">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                      </svg>
                      {planModalService.cancellationPolicy}
                    </p>
                  )}
                </div>
                <div className="modal-actions">
                  <button 
                    className="select-plan-btn"
                    onClick={() => {
                      setSelectedPlan(planModalService);
                      closePlanModal();
                    }}
                  >
                    Select This Plan - {planModalService.price}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sticky Mobile CTA */}
      <div className="sticky-mobile-cta">
        <div className="sticky-cta-info">
          <span className="sticky-cta-name">{currentService.name}</span>
          <span className={`sticky-cta-price ${!effectiveSelectedPlan ? 'sticky-cta-price--muted' : ''}`}>
            {stickyCtaSecondaryLabel}
          </span>
        </div>
        <button className="sticky-cta-btn" onClick={handleBooking}>
          {stickyCtaPrimaryLabel}
        </button>
      </div>
        </>
      )}
      
      {/* Platform Site Price Modal */}
      {tempPlatformPricing && (
        <PlatformSitePriceModal
          isOpen={showPlatformPriceModal}
          onClose={handlePlatformPriceCancel}
          onConfirm={handlePlatformPriceConfirm}
          serviceName={tempPlatformPricing.serviceName}
        />
      )}
      
      {/* Booking Hold Modal */}
      <BookingHoldModal
        isOpen={showBookingHoldModal}
        message={bookingHoldMessage}
        onConfirm={handleBookingHoldConfirm}
      />

      {/* Toast notifications */}
      {toast && (
        <div className={`app-toast app-toast--${toast.type}`} role="status" aria-live="polite">
          <span className="app-toast-icon">
            {toast.type === 'success' ? '✓' : toast.type === 'error' ? '✕' : 'ℹ'}
          </span>
          <span className="app-toast-message">{toast.message}</span>
          <button className="app-toast-close" onClick={() => setToast(null)} aria-label="Dismiss">×</button>
        </div>
      )}
    </div>
  );
};

export default BookingPage;